import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OwnableInfo, UpdateOwnableParams} from 'create-components/OwnableConstants';
import OwnableSettingsModal from 'create-components/OwnableSettingsModal';
import {toServerCollaborators} from 'create-components/OwnableUtil';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {HasOwnablePermissionsGraphQL} from '../../../../graphql-fragments/auth/__generated/HasOwnablePermissionsGraphQL';
import {updateConnectionOwnableInfo} from '../ConnectionDAO';
import {
  ConnectionOwnerAndCollaboratorsQuery,
  ConnectionOwnerAndCollaboratorsQueryVariables,
} from './__generated/ConnectionOwnerAndCollaboratorsQuery';

export interface ConnectionCollaboratorsModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  connectionId: string;
  connectionOwnerAndCollaboratorInfo: OwnableInfo;
  permissions: HasOwnablePermissionsGraphQL['permissions'];
}

export const CONNECTION_OWNER_AND_COLLABORATORS_QUERY: DocumentNode = gql`
  query ConnectionOwnerAndCollaboratorsQuery($connectionId: StrictString!)
  @skipAudit
  @engComponent(name: INSTRUMENTS) {
    connection(id: $connectionId) {
      ... on OwnableInterface {
        ...OwnerAndCollaboratorInfoGraphQL
        ...HasOwnablePermissionsGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('OwnerAndCollaboratorInfo').fragment}
  ${getFragmentWrapper('HasOwnablePermissions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function QueryingConnectionCollaboratorsModal(
  props: Omit<ConnectionCollaboratorsModalProps, 'connectionOwnerAndCollaboratorInfo' | 'permissions'>
): JSX.Element {
  return (
    <BenchlingQuery<ConnectionOwnerAndCollaboratorsQuery, ConnectionOwnerAndCollaboratorsQueryVariables>
      query={CONNECTION_OWNER_AND_COLLABORATORS_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{connectionId: props.connectionId}}
    >
      {(data) => {
        assert(!isInaccessible(data.connection), 'Expected connection to be accessible');

        const {connection} = data;
        const connectionOwnerAndCollaboratorInfo: OwnableInfo = {
          id: connection.id,
          name: connection.name,
          owner: connection.ownerJson,
          ownerInfo: connection.ownerInfoJson,
          collaborators: connection.collaboratorsJson,
        };

        return (
          <ConnectionCollaboratorsModal
            {...props}
            connectionOwnerAndCollaboratorInfo={connectionOwnerAndCollaboratorInfo}
            permissions={data.connection.permissions}
          />
        );
      }}
    </BenchlingQuery>
  );
}
/**
 * Modal component to update owner and collaborators for a connection
 */
function ConnectionCollaboratorsModal(props: ConnectionCollaboratorsModalProps): JSX.Element {
  async function handleUpdate(data: UpdateOwnableParams): Promise<void> {
    await updateConnectionOwnableInfo(props.connectionId, {
      collaborators: toServerCollaborators(data.collaborators!),
      owner: data.owner.handle,
      ownerInfo: data.ownerInfo,
    });
  }

  return (
    <OwnableSettingsModal
      ownableType={ModelTypeConstants.CONNECTION}
      existingOwnable={props.connectionOwnerAndCollaboratorInfo}
      onRequestClose={props.onRequestClose}
      navigateOnSuccess={false}
      doSubmit={(data: UpdateOwnableParams) => handleUpdate({...data, collaborators: data.collaborators!})}
      enableName={false}
      enableDescription={false}
      isReadOnly={!props.permissions.admin}
    />
  );
}

export default QueryingConnectionCollaboratorsModal;
